import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useApi from '../api/base/useApi';
import { toast } from 'react-hot-toast';
import { chatActions } from '../../lib/redux/chat/slice';
import { UPLOAD_MAX_SIZE } from '../../utils/shared';
import { Error } from '../../modules/alerts';

export default function useDocumentUpload() {
  const embed = useSelector((state) => state.embed);
  const [isDone, setIsDone] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth?.data);
  const chat = useSelector((state) => state.chat);

  const api = useApi();

  const handleSubmitAttachment = async () => {
    const sessionId = chat?.sessionId;
    const url = `/api/ai-index/orgs/${auth?.tenant?.key}/users/${auth?.user?.user_nicename}/documents/train/sessions/${sessionId}/`;
    const fileInput = document.getElementById('attachment-upload');
    const formData = new FormData();
    formData.append('file', fileInput.files[0]);

    setUploading(true);
    setIsDone(false);
    const response = await api.post(url, formData, true);
    if (response?.data) {
      setIsDone(true);
    } else {
      toast.custom(Error(response?.error || 'Could not upload file'), {
        ariaProps: { role: 'alert' },
      });
    }
    setUploading(false);
    document.getElementById('attachment-upload').value = '';
  };

  const getFilePreviewURL = (file) => {
    const reader = new FileReader();

    if (file.type.startsWith('image/')) {
      reader.onload = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
      return;
    }

    if (file.type === 'application/pdf') {
      const fileURL = URL.createObjectURL(file);
      setPreviewUrl(fileURL);
      return;
    }

    setPreviewUrl(
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '20px 12px',
          borderRadius: '10px',
          backgroundColor: '#F1F3F5',
          color: '#1C1C1C',
          fontFamily: 'sans-serif',
          fontSize: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <span style={{ fontSize: '32px' }}>📄</span>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            lineHeight: '1.2',
          }}
        >
          <span style={{ fontWeight: 500, color: '#1C1C1C' }}>
            {file?.name}
          </span>
          <span style={{ color: '#6C757D', fontSize: '12px' }}>
            {file?.name?.split('.')?.pop()}
          </span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isDone) {
      const newMessages = [...chat.messages];
      newMessages.push({
        type: 'human',
        content: file?.name,
        url: previewUrl,
        fileType: file.type,
        contentType: 'file',
      });
      if (embed?.mode === 'advanced' && embed?.advanceActiveTab) {
        dispatch(
          chatActions.tabbedMessagesUpdated({
            ...chat.tabbedMessages,
            [embed.advanceActiveTab]: newMessages,
          })
        );
      } else {
        dispatch(chatActions.messagesUpdated(newMessages));
      }
    }
  }, [isDone]);

  const handlePromptFileUploadBtnClick = (event) => {
    document.getElementById('attachment-upload')?.click();
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files?.length > 0) {
      // if (
      //   !DOCUMENTS_EXTENSIONS.includes('.' + files[0].name.split('.').pop())
      // ) {
      //   toast.custom(Error("The uploaded file extension isn't accepted!"), {
      //     ariaProps: { role: 'alert' },
      //   });
      //   document.getElementById('attachment-upload').value = '';
      //   return;
      // }
      if (files[0].size > UPLOAD_MAX_SIZE) {
        toast.custom(
          Error(
            `The uploaded file shouldn't exceed ${UPLOAD_MAX_SIZE / (1024 * 1024)} MB.`
          ),
          {
            ariaProps: { role: 'alert' },
          }
        );
        document.getElementById('attachment-upload').value = '';
        return;
      }
      setFile(files[0]);
      getFilePreviewURL(files[0]);
      handleSubmitAttachment();
    }
  };

  return { handleFileChange, uploading, file, handlePromptFileUploadBtnClick };
}
